/**
 * INT-1127: Award-amount entry for the staff "Complete Application" modal.
 *
 * Staff think in dollars ("the scholarship granted to the family"), but the
 * financial-aid decision backend stores `awardedPercent` and derives
 * `awardedAmount = (awardedPercent / 100) * ystEligibleTuition`
 * (see packages/financial-aid-store/src/services/decisions/make-decision.ts).
 *
 * These helpers convert between the two representations against the same
 * eligible-tuition figure the backend uses, so what staff see is what gets
 * recorded.
 */

export const AWARD_PERCENT_MIN = 0;
export const AWARD_PERCENT_MAX = 100;

/** Percent deltas below this are treated as "matches the recommendation". */
const AWARD_PERCENT_EPSILON = 0.005;

const roundTo = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

/** Percent is stored with 2 decimals so cent-level dollar entry round-trips. */
export const roundAwardPercent = (value: number): number => roundTo(value, 2);

/** Dollars are rounded to cents. */
export const roundAwardAmount = (value: number): number => roundTo(value, 2);

/** Parses a staff-typed value, tolerating `$`, `%`, commas and spaces. */
export const parseAwardInput = (raw: string): number | null => {
  const normalized = String(raw ?? '').replace(/[$%,\s]/g, '');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const percentToAwardAmount = (percent: number, eligibleTuition: number): number =>
  eligibleTuition > 0 ? roundAwardAmount((percent / 100) * eligibleTuition) : 0;

export const awardAmountToPercent = (amount: number, eligibleTuition: number): number =>
  eligibleTuition > 0 ? roundAwardPercent((amount / eligibleTuition) * 100) : 0;

export const isSameAwardPercent = (left: number, right: number): boolean =>
  Math.abs(left - right) < AWARD_PERCENT_EPSILON;

/** Input-field text (no currency symbol, no thousands separators). */
export const formatAwardPercentInput = (percent: number): string =>
  String(roundAwardPercent(percent));

export const formatAwardAmountInput = (amount: number): string => String(roundAwardAmount(amount));

/** Display text for derived/confirmation copy. */
export const formatAwardCurrency = (amount: number): string => {
  const rounded = roundAwardAmount(amount);
  const hasCents = !Number.isInteger(rounded);
  return rounded.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
};

export const formatAwardPercentLabel = (percent: number): string =>
  `${roundAwardPercent(percent)}%`;

export type AwardEntryDecision =
  | {
    ok: true;
    decisionType: 'approve' | 'override';
    awardedPercent: number;
    awardedAmount: number;
    overrideJustification?: string;
  }
  | { ok: false; message: string };

export type AwardEntryInput = {
  percentInput: string;
  recommendedPercent: number;
  eligibleTuition: number;
  justification: string;
};

/**
 * Resolves what should be sent to `makeFinancialAidDecision`.
 *
 * - Matches the recommendation -> plain `approve`.
 * - Differs from the recommendation -> `override`, which the backend requires
 *   to carry both `awardedPercent` and `overrideJustification`.
 */
export const resolveAwardEntryDecision = ({
  percentInput,
  recommendedPercent,
  eligibleTuition,
  justification,
}: AwardEntryInput): AwardEntryDecision => {
  const parsed = parseAwardInput(percentInput);
  if (parsed == null) {
    return { ok: false, message: 'Enter the scholarship award before approving.' };
  }
  if (parsed < AWARD_PERCENT_MIN || parsed > AWARD_PERCENT_MAX) {
    return {
      ok: false,
      message: `The award must be between ${AWARD_PERCENT_MIN}% and ${AWARD_PERCENT_MAX}% of eligible tuition.`,
    };
  }

  const awardedPercent = roundAwardPercent(parsed);
  const awardedAmount = percentToAwardAmount(awardedPercent, eligibleTuition);

  if (isSameAwardPercent(awardedPercent, roundAwardPercent(recommendedPercent))) {
    return { ok: true, decisionType: 'approve', awardedPercent, awardedAmount };
  }

  const overrideJustification = justification.trim();
  if (!overrideJustification) {
    return {
      ok: false,
      message: `Add a short reason for awarding ${formatAwardPercentLabel(awardedPercent)} instead of the ${formatAwardPercentLabel(recommendedPercent)} recommendation.`,
    };
  }

  return {
    ok: true,
    decisionType: 'override',
    awardedPercent,
    awardedAmount,
    overrideJustification,
  };
};
